import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/layout";
import { Separator } from "@/components/ui/separator";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Course, Module } from "@/types/course";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { CourseHeader } from "@/components/course/CourseHeader";
import { CourseInfoTab } from "@/components/course/CourseInfoTab";
import { CourseContentTab } from "@/components/course/CourseContentTab";
import { CourseLoading } from "@/components/course/CourseLoading";
import { CourseNotFound } from "@/components/course/CourseNotFound";
import ReviewsManagement from "@/components/course/ReviewsManagement";
import { devLog, devError } from "@/utils/devLog";

export default function CourseEditor() {
  const { courseId } = useParams<{ courseId: string }>();
  const queryClient = useQueryClient();
  
  const [modules, setModules] = useState<Module[]>([]);
  const [isPublishing, setIsPublishing] = useState(false);

  const { 
    data: course, 
    isLoading: isLoadingCourse,
    error: courseError 
  } = useQuery({
    queryKey: ['course', courseId],
    queryFn: async () => {
      if (!courseId) throw new Error("Course ID is required");
      
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('id', courseId)
        .single();
        
      if (error) throw error;
      return data as Course;
    }
  });

  const { 
    data: fetchedModules, 
    isLoading: isLoadingModules,
    error: modulesError 
  } = useQuery({
    queryKey: ['modules', courseId],
    queryFn: async () => {
      if (!courseId) throw new Error("Course ID is required");
      
      const { data: modules, error: modulesError } = await supabase
        .from('modules')
        .select('*')
        .eq('course_id', courseId)
        .order('order', { ascending: true });
        
      if (modulesError) throw modulesError;
      
      const modulesWithLessons: Module[] = [];
      
      for (const module of modules) {
        const { data: lessons, error: lessonsError } = await supabase
          .from('lessons')
          .select('*')
          .eq('module_id', module.id);
          
        if (lessonsError) throw lessonsError;
        
        const lessonsWithDetails = [];
        
        for (const lesson of lessons) {
          const { data: files } = await supabase
            .from('lesson_files')
            .select('*')
            .eq('lesson_id', lesson.id);
            
          const { data: faqs } = await supabase
            .from('lesson_faqs')
            .select('*')
            .eq('lesson_id', lesson.id);
            
          lessonsWithDetails.push({
            ...lesson,
            files: files || [],
            faqs: faqs || []
          });
        }
        
        modulesWithLessons.push({
          ...module,
          lessons: lessonsWithDetails
        });
      }
      
      return modulesWithLessons;
    },
    enabled: !!courseId
  });

  useEffect(() => {
    if (fetchedModules) {
      setModules(fetchedModules);
    }
  }, [fetchedModules]);

  useEffect(() => {
    if (courseError) {
      devLog("Error fetching course:", courseError);
      toast.error("Failed to load course details");
    }
    
    if (modulesError) {
      devLog("Error fetching modules:", modulesError);
      toast.error("Failed to load course content");
    }
  }, [courseError, modulesError]);

  const handleModulesChange = (updatedModules: Module[]) => {
    setModules(updatedModules);
    queryClient.invalidateQueries({
      queryKey: ['modules', courseId],
    });
  };

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
    staleTime: Infinity,
  });

  const { data: profile } = useQuery({
    queryKey: ['userProfile', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();

      if (error) {
        devLog("Error fetching profile:", error);
        return null;
      }

      return data;
    },
    staleTime: Infinity,
    enabled: !!user?.id,
  });

  if (isLoadingCourse) {
    return (
      <DashboardLayout>
        <CourseLoading />
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout>
        <CourseNotFound />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <CourseHeader 
        course={course} 
        isPublishing={isPublishing} 
        setIsPublishing={setIsPublishing}
        profile={profile}
      />

      <Tabs defaultValue="info" className="w-full mt-2 mb-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="info">Course Info</TabsTrigger>
          <TabsTrigger value="content">Course Content</TabsTrigger>
          <TabsTrigger value="reviews">Ratings & Reviews</TabsTrigger>
        </TabsList>
        
        <Separator className="mb-6 mt-2" />
        
        <TabsContent value="info">
          <CourseInfoTab courseId={courseId!} />
        </TabsContent>
          
        <TabsContent value="content">
          <CourseContentTab 
            courseId={courseId!}
            modules={modules}
            isLoadingModules={isLoadingModules}
            onModulesChange={handleModulesChange}
          />
        </TabsContent>
        
        <TabsContent value="reviews">
          <Card className="border-0 shadow-none">
            <CardContent className="p-0">
              {courseId && <ReviewsManagement courseId={courseId} />}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
